import { Button, cn } from "@win-reality/react-ui";
import { EllipsisVertical } from "lucide-react";
import { useState } from "react";

import { MAX_RUNS_PER_PLAY, OUTS_PER_INNING } from "../../constants/game";
import { CONTENT_WIDTH } from "../../constants/layout";
import { ScorekeepingActions } from "../../hooks/useScorekeepingActions";
import { GameFullResponse } from "../../types/gameFull";
import { GameRosters } from "../../types/roster";
import {
  getPlateAppearanceEditValues,
  PlateAppearanceEditValues,
} from "../../utils/plateAppearanceEditor";
import { getActivitiesForPA } from "../../utils/plateAppearanceFeed";
import {
  findLineupPlayer,
  getLineupSide,
  toPlayerRef,
} from "../../utils/playerChange";
import { formatPlayerName } from "../../utils/playerName";
import { TeamDisplays } from "../../utils/teamColors";
import { BaseRunners } from "../BaseRunners";
import { ResultRow, Stepper } from "../ResultControls";
import { JerseyNumberDialog } from "./JerseyNumberDialog";
import { PlayerCard } from "./PlayerCard";
import {
  PlayerChangeDialog,
  PlayerChangeRole,
  PlayerChangeTab,
} from "./PlayerChangeDialog";

/** "Cameron Williamson" -> "C. Williamson" */
const shortName = (name: string) => {
  const [first, ...rest] = name.trim().split(/\s+/);
  return rest.length ? `${first.charAt(0)}. ${rest.join(" ")}` : first;
};

const SectionLabel = ({ children }: { children: string }) => (
  <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
    {children}
  </h4>
);

type PlateAppearanceEditorProps = {
  data: GameFullResponse;
  plateAppearanceId: string;
  onUpdate: (
    plateAppearanceId: string,
    values: PlateAppearanceEditValues,
  ) => Promise<boolean>;
  rosters: GameRosters;
  teamDisplays: TeamDisplays;
  /** Saves player changes (jersey number, change hitter, sub out) via omnibus */
  actions: Pick<
    ScorekeepingActions,
    | "editJerseyNumber"
    | "changeHitter"
    | "subOut"
    | "addRosterPlayers"
    | "isWaitingOnTag"
  >;
  onManageLineups: () => void;
};

/**
 * At-bat editor. For an ended plate appearance this is the Post At-Bat Game
 * State (spec 3.5): hitter, pitcher and the last pitch's results of play.
 * Its inning isn't editable: moving a plate appearance to another inning
 * needs a game service endpoint. For the active at-bat (spec 2.3) only the
 * hitter and pitcher are editable. Player changes save right away; Update
 * saves the rest.
 */
export const PlateAppearanceEditor = ({
  data,
  plateAppearanceId,
  onUpdate,
  rosters,
  teamDisplays,
  actions,
  onManageLineups,
}: PlateAppearanceEditorProps) => {
  const [values, setValues] = useState(() =>
    getPlateAppearanceEditValues(data, plateAppearanceId),
  );
  // Player whose jersey number is being edited (Player Details)
  const [editingRole, setEditingRole] = useState<PlayerChangeRole | null>(null);
  // Player card Change / Sub Out modal
  const [changing, setChanging] = useState<{
    role: PlayerChangeRole;
    tab: PlayerChangeTab;
  } | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  if (!values) return null;

  const isActive = plateAppearanceId === data.game.currentPlateAppearanceId;
  // On the active at-bat, Change and Sub Out send a player tag carrying the
  // local count, outs and score. Until a saved pitch tag comes back, those are
  // stale, and the player tag would roll that pitch back in the game service.
  const isPlayerChangeLocked = isActive && actions.isWaitingOnTag;
  const playerChangeLock = {
    isDisabled: isPlayerChangeLocked,
    title: isPlayerChangeLocked
      ? "Waiting for the last pitch's tag to save"
      : undefined,
  };

  // Results of play are stored on the last pitch, so they need one
  const hasResults = Boolean(
    getActivitiesForPA(data, plateAppearanceId).at(-1)?.resultingState,
  );

  const update = (changes: Partial<PlateAppearanceEditValues>) =>
    setValues((prev) => prev && { ...prev, ...changes });

  // Team by the half-inning, unless only the other team's lineup has them
  const battingSide = values.isTopOfInning ? "away" : "home";
  const hitterSide = getLineupSide(data, values.hitter.playerId, battingSide);
  const pitcherSide = getLineupSide(
    data,
    values.pitcher.playerId,
    hitterSide === "away" ? "home" : "away",
  );
  const sideOf = (role: PlayerChangeRole) =>
    role === "hitter" ? hitterSide : pitcherSide;
  const playerOf = (role: PlayerChangeRole) =>
    role === "hitter" ? values.hitter : values.pitcher;

  // The game service keeps a jersey number on the team's lineup row, so only
  // a player on that lineup has one to edit
  const isOnTeamLineup = (role: PlayerChangeRole) => {
    const { playerId } = playerOf(role);
    return (
      Boolean(playerId) &&
      data.lineUp[sideOf(role)].some((p) => p.playerId === playerId)
    );
  };

  /** Runs a player change with the saving state; resolves to its result. */
  const save = async <T,>(change: () => Promise<T>) => {
    setIsSaving(true);
    try {
      return await change();
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditJerseyNumber = async (
    role: PlayerChangeRole,
    jerseyNumber: string,
  ) => {
    const isSaved = await save(() =>
      actions.editJerseyNumber(
        sideOf(role),
        playerOf(role).playerId,
        jerseyNumber,
      ),
    );
    if (isSaved) setEditingRole(null);
  };

  const teamLabel = (side: "away" | "home") =>
    `${data.game.teams[side].name} (${side === "away" ? "Away" : "Home"})`;

  /** Team label and player card; only hitters have Change, and only known players have Player Details. */
  const playerSection = (role: PlayerChangeRole) => {
    const isHitter = role === "hitter";
    const side = sideOf(role);
    const player = playerOf(role);
    const info = findLineupPlayer(data, player.playerId, side);
    return (
      <div key={role} className="space-y-3">
        <SectionLabel>{teamLabel(side)}</SectionLabel>
        <PlayerCard
          data-testid="plate-appearance-editor-player-panel"
          data-player-role={role}
          data-player-id={player.playerId}
          playerRole={isHitter ? "Hitter" : "Pitcher"}
          name={formatPlayerName(player.playerName)}
          jerseyNumber={info?.jerseyNumber}
          teamColor={teamDisplays[side].color}
          lineupPosition={isHitter ? info?.battingOrder : undefined}
          actions={[
            ...(isOnTeamLineup(role)
              ? [
                  {
                    label: "Player Details",
                    onClick: () => setEditingRole(role),
                    testId: "plate-appearance-editor-edit-player-button",
                  },
                ]
              : []),
            ...(isHitter
              ? [
                  {
                    label: "Change",
                    onClick: () => setChanging({ role, tab: "change" }),
                    ...playerChangeLock,
                    testId: "plate-appearance-editor-change-player-button",
                  },
                ]
              : []),
            {
              label: "Sub Out",
              onClick: () => setChanging({ role, tab: "subOut" }),
              ...playerChangeLock,
              testId: "plate-appearance-editor-sub-out-button",
            },
          ]}
        />
      </div>
    );
  };

  return (
    <div
      data-testid="plate-appearance-editor-panel"
      data-plate-appearance-id={plateAppearanceId}
      className="flex h-full w-full flex-col bg-tagging-panel transition-colors"
    >
      {/* Header */}
      <div
        className={cn(
          CONTENT_WIDTH,
          "flex items-start justify-between px-6 pb-2 pt-6",
        )}
      >
        <h2 className="text-lg font-semibold leading-snug text-gray-900 dark:text-white">
          {isActive ? "Active At-Bat" : "Post At-Bat Game State"}
          <br />
          {shortName(formatPlayerName(values.pitcher.playerName))} vs{" "}
          {shortName(formatPlayerName(values.hitter.playerName))}
        </h2>
        {/* TODO: Post at-bat actions menu */}
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label="Post at-bat actions"
          data-testid="plate-appearance-editor-actions-menu-button"
          className="text-gray-900 dark:text-white"
        >
          <EllipsisVertical className="h-5 w-5" />
        </Button>
      </div>

      <div className="tagging-scrollbar flex-1 overflow-y-auto">
        <div className={cn(CONTENT_WIDTH, "space-y-6 px-6 pb-6 pt-4")}>
          {playerSection("hitter")}
          {playerSection("pitcher")}

          {!isActive && (
            <div className="space-y-3">
              <h4 className="text-xs font-medium uppercase tracking-wide text-tagging-muted">
                Results of Play
              </h4>
              <div className="space-y-2">
                <ResultRow label="Outs Recorded">
                  <Stepper
                    value={values.outsRecorded}
                    max={OUTS_PER_INNING}
                    disabled={!hasResults}
                    onChange={(outsRecorded) => update({ outsRecorded })}
                    valueTestId="plate-appearance-editor-outs-recorded-value"
                    incrementTestId="plate-appearance-editor-outs-recorded-increment-button"
                    decrementTestId="plate-appearance-editor-outs-recorded-decrement-button"
                  />
                </ResultRow>
                <ResultRow label="Runs Scored">
                  <Stepper
                    value={values.runsScored}
                    max={MAX_RUNS_PER_PLAY}
                    disabled={!hasResults}
                    onChange={(runsScored) => update({ runsScored })}
                    valueTestId="plate-appearance-editor-runs-scored-value"
                    incrementTestId="plate-appearance-editor-runs-scored-increment-button"
                    decrementTestId="plate-appearance-editor-runs-scored-decrement-button"
                  />
                </ResultRow>
                <ResultRow label="Base Runners at End of Play">
                  <BaseRunners
                    data-testid="plate-appearance-editor-base-runners-panel"
                    editable={hasResults}
                    baserunners={values.baseRunners}
                    onChange={(baseRunners) => update({ baseRunners })}
                  />
                </ResultRow>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Mounted per open so the form starts from the player's current number */}
      {editingRole && (
        <JerseyNumberDialog
          playerName={formatPlayerName(playerOf(editingRole).playerName)}
          jerseyNumber={
            findLineupPlayer(
              data,
              playerOf(editingRole).playerId,
              sideOf(editingRole),
            )?.jerseyNumber
          }
          isSaving={isSaving}
          onCancel={() => setEditingRole(null)}
          onSave={(jerseyNumber) =>
            void handleEditJerseyNumber(editingRole, jerseyNumber)
          }
        />
      )}

      {changing && (
        <PlayerChangeDialog
          role={changing.role}
          initialTab={changing.tab}
          side={sideOf(changing.role)}
          team={teamDisplays[sideOf(changing.role)]}
          player={playerOf(changing.role)}
          data={data}
          rosters={rosters}
          onClose={() => setChanging(null)}
          isSaving={isSaving}
          onChangeHitter={async (player) => {
            const isSaved = await save(() =>
              actions.changeHitter({ plateAppearanceId, player }),
            );
            if (!isSaved) return;
            update({ hitter: toPlayerRef(player) });
            setChanging(null);
          }}
          onSubOut={async (player) => {
            const sub = await save(() =>
              actions.subOut({
                side: sideOf(changing.role),
                role: changing.role,
                plateAppearanceId,
                outPlayer: playerOf(changing.role),
                inPlayer: player,
              }),
            );
            if (!sub) return;
            update(
              changing.role === "hitter"
                ? { hitter: toPlayerRef(sub) }
                : { pitcher: toPlayerRef(sub) },
            );
            setChanging(null);
          }}
          onAddRosterPlayers={(players) =>
            actions.addRosterPlayers(sideOf(changing.role), players)
          }
          onManageLineup={() => {
            setChanging(null);
            onManageLineups();
          }}
        />
      )}

      {/* Footer: player changes save right away; Update saves the rest */}
      {!isActive && (
        <div
          className={cn(
            // Border
            "border-t border-tagging-option-border",
            // Background
            "bg-tagging-panel",
          )}
        >
          <div className={cn(CONTENT_WIDTH, "flex justify-center px-6 py-4")}>
            <Button
              variant="secondary-flat-light"
              onClick={() => onUpdate(plateAppearanceId, values)}
              data-testid="plate-appearance-editor-update-button"
              className="min-w-[192px] rounded-full"
            >
              Update
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlateAppearanceEditor;
